import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CustomerDetailRecord, CustomerStatus, CustomerType } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { CollectionTable } from '@renderer/shared/components/collection-table';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon, normalizeBusinessIconName } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  SearchableSelectField,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { openExternal, toTelUrl } from '@renderer/shared/utils/open-external';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { deleteCustomers, getCustomerById, updateCustomer } from './customers-repository';
import { selectCustomerEditorModel } from './customers-selectors';
import { useCustomersViewStore } from './customers-view-store';

interface CustomerDraft {
  name: string;
  status: CustomerStatus;
  type: CustomerType;
  customerCategoryId: string;
  legalName: string;
  cui: string;
  registrationNumber: string;
  vatPayer: '' | 'true' | 'false';
  email: string;
  website: string;
  shortDescription: string;
}

function toDraft(detail: CustomerDetailRecord): CustomerDraft {
  return {
    name: detail.customer.name,
    status: detail.customer.status,
    type: detail.customer.type,
    customerCategoryId: detail.customer.customerCategoryId ?? '',
    legalName: detail.customer.legalName,
    cui: detail.customer.cui ?? '',
    registrationNumber: detail.customer.registrationNumber ?? '',
    vatPayer: detail.customer.vatPayer === null ? '' : detail.customer.vatPayer ? 'true' : 'false',
    email: detail.customer.email ?? '',
    website: detail.customer.website ?? '',
    shortDescription: detail.customer.shortDescription ?? ''
  };
}

export function CustomerEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useCustomersViewStore();
  const [detail, setDetail] = useState<CustomerDetailRecord | null>(null);
  const [draft, setDraft] = useState<CustomerDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const savedDraft = useMemo(() => (detail ? toDraft(detail) : null), [detail]);

  useEffect(() => {
    if (!customerId) {
      setError('Missing customer id.');
      return;
    }

    let active = true;
    void getCustomerById(customerId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Customer not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setRouteDetailTitle(nextDetail.customer.name);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Customer unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [customerId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'customers' || !detail || !detailRequest.ids.includes(detail.customer.id)) {
          return;
        }

        void deleteCustomers({ ids: [detail.customer.id] }).then(() => navigate('/customers'));
      }),
    [detail, navigate]
  );

  useRegisterEditorSaveHandler(
    detail && draft && savedDraft
      ? () => {
          if (isSaving) {
            return;
          }

          const nextIsDirty = !areDraftsEqual(draft, savedDraft);
          if (!nextIsDirty) {
            return;
          }

          void handleSave();
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('customers.title')} description={error} />
      </div>
    );
  }

  if (!detail) {
    return <div className="av-page" />;
  }

  const model = selectCustomerEditorModel(detail, { locale, t });
  const customerIdToDelete = detail.customer.id;
  const primaryPhone = model.primaryContact?.phone ?? null;
  const isDirty = draft && savedDraft ? !areDraftsEqual(draft, savedDraft) : false;
  const statusOptions = (['active', 'inactive', 'lead', 'archived'] satisfies CustomerStatus[]).map((value) => ({
    value,
    label: t(`enums.customerStatus.${value}`)
  }));
  const typeOptions = (['company', 'sole_trader', 'individual'] satisfies CustomerType[]).map((value) => ({
    value,
    label: t(`enums.customerType.${value}`)
  }));
  const customerCategoryOptions = [
    { value: '', label: t('customers.filters.unassignedCategory'), icon: 'times' as const },
    ...detail.customerCategories
      .filter((category) => !category.archivedAt || category.id === (draft?.customerCategoryId ?? ''))
      .map((category) => ({
        value: category.id,
        label: category.name,
        icon: normalizeBusinessIconName(category.iconName),
        description: category.archivedAt ? t('common.archived') : undefined
      }))
  ];

  async function handleDeleteCustomer(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteCustomers({ ids: [customerIdToDelete] });
    navigate('/customers');
  }

  async function handleSave(): Promise<void> {
    if (!draft || !detail) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const detailResponse = await updateCustomer({
        id: detail.customer.id,
        name: draft.name.trim(),
        status: draft.status,
        type: draft.type,
        customerCategoryId: draft.customerCategoryId || null,
        legalName: draft.legalName.trim(),
        cui: draft.cui.trim() ? draft.cui.trim() : null,
        registrationNumber: draft.registrationNumber.trim() ? draft.registrationNumber.trim() : null,
        vatPayer: draft.vatPayer === '' ? null : draft.vatPayer === 'true',
        email: draft.email.trim() ? draft.email.trim() : null,
        website: draft.website.trim() ? draft.website.trim() : null,
        shortDescription: draft.shortDescription.trim() ? draft.shortDescription.trim() : null
      });

      setDetail(detailResponse);
      setDraft(toDraft(detailResponse));
      setRouteDetailTitle(detailResponse.customer.name);
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel(): void {
    if (!detail) {
      return;
    }

    setSaveError(null);
    setDraft(toDraft(detail));
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDeleteCustomer()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
        >
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="customers" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{model.title}</h2>
                <div className="av-editor-hero-card__meta">
                  {model.favoriteLabel ? <StatusBadge label={model.favoriteLabel} tone="warning" /> : null}
                  <StatusBadge label={model.statusLabel} tone={model.statusTone} />
                  <span className="av-category-chip">
                    <Icon name={model.categoryIconName} />
                    <span>{model.categoryLabel}</span>
                    {model.categoryArchived ? <em>{t('common.archived')}</em> : null}
                  </span>
                </div>
                {model.ratingLabel ? (
                  <div className="av-rating-pill">
                    <Icon name="star" />
                    <span>{model.ratingLabel}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              {model.siteLinks.map((link) => (
                <div key={link.key} className="av-endpoint-row">
                  <Icon name={link.icon} />
                  <span>
                    <strong>{link.label}:</strong> {link.value ?? t('common.notSet')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={model.activeSinceTitle} actions={<OverflowMenuButton />}>
          <div className="av-summary-stack">
            <div className="av-summary-highlight">
              <strong>{model.completedProjectsLabel}</strong>
              <span>{t('customers.editor.summary.generatedRevenue')}</span>
              <em>{model.generatedRevenueLabel}</em>
            </div>
            <button className="av-secondary-button av-secondary-button--full" type="button">
              <Icon name="addNote" />
              <span>{t('customers.editor.actions.addNote')}</span>
            </button>
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('customers.editor.tabs.general'), icon: 'projects' },
            { id: 'projects', label: t('customers.editor.tabs.projects'), icon: 'projects' },
            { id: 'contracts', label: t('customers.editor.tabs.contracts'), icon: 'contracts' },
            { id: 'invoices', label: t('customers.editor.tabs.invoices'), icon: 'invoices' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveError ? <div className="av-inline-error">{saveError}</div> : null}
            {editorTab === 'general' ? (
              <>
                <EditorSectionCard
                  title={t('customers.editor.sections.general')}
                  actions={
                    <div className="av-inline-metadata">
                      <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!isDirty || isSaving}>
                        {t('common.cancel')}
                      </button>
                      <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving}>
                        {t('common.applyChanges')}
                      </button>
                    </div>
                  }
                >
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.name')}</span>
                      <TextField
                        value={draft?.name ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, name: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.customerCategory')}</span>
                      <SearchableSelectField
                        clearLabel={t('common.clear')}
                        emptyLabel={t('common.noResults')}
                        options={customerCategoryOptions}
                        placeholder={t('customers.filters.categoryPlaceholder')}
                        searchPlaceholder={t('customers.filters.searchCategory')}
                        value={draft?.customerCategoryId ?? ''}
                        onValueChange={(value) =>
                          setDraft((current) => (current ? { ...current, customerCategoryId: value } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.status')}</span>
                      <SelectField
                        value={draft?.status ?? detail.customer.status}
                        options={statusOptions}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, status: event.target.value as CustomerStatus } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.legalType')}</span>
                      <SelectField
                        value={draft?.type ?? detail.customer.type}
                        options={typeOptions}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, type: event.target.value as CustomerType } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.legalName')}</span>
                      <TextField
                        value={draft?.legalName ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, legalName: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.cui')}</span>
                      <TextField
                        value={draft?.cui ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, cui: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.registrationNumber')}</span>
                      <TextField
                        value={draft?.registrationNumber ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, registrationNumber: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.vatPayer')}</span>
                      <SelectField
                        value={draft?.vatPayer ?? ''}
                        options={[
                          { value: '', label: t('common.notSet') },
                          { value: 'true', label: t('common.yes') },
                          { value: 'false', label: t('common.no') }
                        ]}
                        onChange={(event) =>
                          setDraft((current) => (current ? { ...current, vatPayer: event.target.value as CustomerDraft['vatPayer'] } : current))
                        }
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.email')}</span>
                      <TextField
                        type="email"
                        value={draft?.email ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, email: value } : current))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('customers.editor.fields.website')}</span>
                      <TextField
                        value={draft?.website ?? ''}
                        onValueChange={(value) => setDraft((current) => (current ? { ...current, website: value } : current))}
                      />
                    </label>
                  </div>

                  <div className="av-form-field">
                    <span>{t('customers.editor.fields.shortDescription')}</span>
                    <TextAreaField
                      rows={4}
                      value={draft?.shortDescription ?? ''}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, shortDescription: value } : current))}
                    />
                  </div>
                </EditorSectionCard>

                <div className="av-customer-secondary-grid">
                  <EditorSectionCard title={t('customers.editor.sections.primaryContact')} actions={<OverflowMenuButton />}>
                    {model.primaryContact ? (
                      <div className="av-summary-stack">
                        <strong>{model.primaryContact.name}</strong>
                        {model.primaryContact.role ? <span>{model.primaryContact.role}</span> : null}
                        <span>{model.primaryContact.email ?? t('common.notSet')}</span>
                        <span>{model.primaryContact.phone ?? t('common.notSet')}</span>
                      </div>
                    ) : (
                      <EmptyState title={t('customers.editor.sections.primaryContact')} description={t('customers.editor.empty.contact')} />
                    )}
                  </EditorSectionCard>

                  <EditorSectionCard title={t('customers.editor.sections.socialMedia')} actions={<OverflowMenuButton />}>
                    <div className="av-social-grid">
                      <div className="av-social-card"><Icon name="facebook" /> Facebook</div>
                      <div className="av-social-card"><Icon name="instagram" /> Instagram</div>
                      <div className="av-social-card"><Icon name="tiktok" /> TikTok</div>
                      <div className="av-social-card"><Icon name="youtube" /> YouTube</div>
                    </div>
                  </EditorSectionCard>
                </div>
              </>
            ) : null}

            {editorTab === 'projects' ? (
              <EditorSectionCard title={t('customers.editor.sections.projects')} actions={<OverflowMenuButton />}>
                {model.projectItems.length === 0 ? (
                  <EmptyState title={t('customers.editor.sections.projects')} description={t('customers.editor.empty.projects')} />
                ) : (
                  <CollectionTable
                    ariaLabel={t('customers.editor.sections.projects')}
                    columns={[
                      { id: 'project', label: t('customers.editor.projects.columns.project') },
                      { id: 'status', label: t('customers.editor.projects.columns.status') },
                      { id: 'updated', label: t('customers.editor.projects.columns.updated') }
                    ]}
                  >
                    {model.projectItems.map((project) => (
                      <tr key={project.id} className="av-collection-table__row">
                        <td className="av-collection-table__cell">
                          <div className="av-summary-stack">
                            <Link className="av-inline-link" to={`/projects/${project.id}`}>
                              <strong>{project.name}</strong>
                              <Icon name="chevron" />
                            </Link>
                            <span>{project.domainLabel ?? t('common.notSet')}</span>
                          </div>
                        </td>
                        <td className="av-collection-table__cell">
                          <StatusBadge label={project.statusLabel} tone={project.statusTone} />
                        </td>
                        <td className="av-collection-table__cell">{project.updatedLabel}</td>
                      </tr>
                    ))}
                  </CollectionTable>
                )}
              </EditorSectionCard>
            ) : null}

            {editorTab === 'contracts' ? (
              <EditorSectionCard title={t('customers.editor.sections.contracts')} actions={<OverflowMenuButton />}>
                {model.contractItems.length === 0 ? (
                  <EmptyState title={t('customers.editor.sections.contracts')} description={t('customers.editor.empty.contracts')} />
                ) : (
                  <ul className="av-compact-list">
                    {model.contractItems.map((contract) => (
                      <li key={contract.id} className="av-compact-list__item">
                        <div>
                          <Link className="av-inline-link" to={`/contracts/${contract.id}`}>
                            <strong>{contract.title}</strong>
                            <Icon name="chevron" />
                          </Link>
                          <span>{contract.metaLabel}</span>
                        </div>
                        <div className="av-inline-metadata">
                          <StatusBadge label={contract.statusLabel} tone={contract.statusTone} />
                          <strong>{contract.amountLabel}</strong>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </EditorSectionCard>
            ) : null}

            {editorTab === 'invoices' ? (
              <EditorSectionCard title={t('customers.editor.sections.invoices')} actions={<OverflowMenuButton />}>
                {model.invoiceItems.length === 0 ? (
                  <EmptyState title={t('customers.editor.sections.invoices')} description={t('customers.editor.empty.invoices')} />
                ) : (
                  <ul className="av-compact-list">
                    {model.invoiceItems.map((invoice) => (
                      <li key={invoice.id} className="av-compact-list__item">
                        <div>
                          <Link className="av-inline-link" to={`/invoices/${invoice.id}`}>
                            <strong>{invoice.label}</strong>
                            <Icon name="chevron" />
                          </Link>
                          <span>{invoice.metaLabel}</span>
                        </div>
                        <div className="av-inline-metadata">
                          <StatusBadge label={invoice.statusLabel} tone={invoice.statusTone} />
                          <strong>{invoice.amountLabel}</strong>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </EditorSectionCard>
            ) : null}
          </>
        }
        rail={
          <>
            <EditorSectionCard title={t('customers.editor.sections.contactAddress')} actions={<OverflowMenuButton />}>
              <div className="av-summary-stack">
                {model.primaryAddress ? (
                  <div className="av-detail-row">
                    <Icon name="location" />
                    <strong>{model.primaryAddress}</strong>
                  </div>
                ) : null}
                {primaryPhone ? (
                  <div className="av-detail-row">
                    <button
                      className="av-inline-link av-inline-link-button"
                      type="button"
                      onClick={() => void openExternal(toTelUrl(primaryPhone))}
                    >
                      <Icon name="phone" />
                      <span>{primaryPhone}</span>
                    </button>
                  </div>
                ) : null}
                {model.primaryContact?.whatsapp ? (
                  <div className="av-detail-row">
                    <Icon name="whatsapp" />
                    <span>{model.primaryContact.whatsapp}</span>
                  </div>
                ) : null}
                <button className="av-secondary-button av-secondary-button--full" type="button">
                  <Icon name="location" />
                  <span>{t('customers.editor.actions.addAddress')}</span>
                </button>
              </div>
            </EditorSectionCard>

            <EditorSectionCard title={t('customers.editor.sections.notes')} actions={<OverflowMenuButton />}>
              {model.featuredNote ? (
                <div className="av-note-preview">
                  <Icon name="document" />
                  <p>{model.featuredNote}</p>
                </div>
              ) : (
                <EmptyState title={t('customers.editor.sections.notes')} description={t('customers.editor.empty.notes')} />
              )}
            </EditorSectionCard>
          </>
        }
      />
    </EditorPageLayout>
  );
}
